import React, { useMemo } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { UserRef } from "@mapmycustomers/shared/types/User";
import { AvatarWithName } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import Table from "@app/component/Table";
import UserPreviewCard from "@app/component/UserPreviewCard";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import { getLayouts, isLoading } from "../../store";
import { setAsDefault } from "../../store/actions";
import AssociationsCell from "../AssociationsCell";
import DeleteLayoutAction from "../DeleteLayoutAction";
import EditAction from "../EditAction";

import styles from "./LayoutsTable.module.scss";

interface Props {
  analyticsIssuer?: AnalyticsService;
  layouts: FormLayout[];
  loading: boolean;
  onSetAsDefault: typeof setAsDefault.request;
}

const LayoutsTable: React.FC<Props> = ({ analyticsIssuer, layouts, loading, onSetAsDefault }) => {
  const intl = useIntl();
  const columns = useMemo(
    () => [
      {
        dataIndex: "name",
        key: "name",
        render: (name: FormLayout["name"], row: FormLayout) => {
          return (
            <Row gutter={layout.spacerS}>
              <Col>{name}</Col>
              {row.default && (
                <Col>
                  <Tooltip
                    title={intl.formatMessage({
                      id: "settings.formLayouts.grid.header.default.tooltip",
                      defaultMessage:
                        "The default layout will be assigned to any new user(s) or if no other layouts are assigned to a given user.",
                      description: "TooltipHeader of name column with default marker",
                    })}
                  >
                    <Tag color="processing">
                      {intl.formatMessage({
                        id: "settings.formLayouts.grid.header.name.default",
                        defaultMessage: "Default",
                        description: "Header of name column with default marker",
                      })}
                    </Tag>
                  </Tooltip>
                </Col>
              )}
            </Row>
          );
        },
        title: intl.formatMessage({
          id: "settings.formLayouts.grid.header.formName",
          defaultMessage: "Form Name",
          description: "Header of Form Name column",
        }),
        width: "15%",
      },
      {
        dataIndex: "id",
        key: "fieldOperation",
        render: (id: number, row: FormLayout) => {
          return (
            <div className={styles.operation}>
              <EditAction layout={row} />
            </div>
          );
        },
        width: "8%",
      },
      {
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (updatedAt: string) => {
          const value = formatDate(parseApiDate(updatedAt), "PPpp");
          return (
            <div>
              <Tooltip placement="top" title={value}>
                <div className={styles.date}>{value}</div>
              </Tooltip>
            </div>
          );
        },
        title: intl.formatMessage({
          id: "settings.formLayouts.grid.header.updatedAt",
          defaultMessage: "Date Last Modified",
          description: "Header of Date Last Modified column",
        }),
        width: "15%",
      },
      {
        dataIndex: "createdAt",
        key: "createdAt",
        render: (createdAt: string) => {
          const value = formatDate(parseApiDate(createdAt), "PPpp");
          return (
            <div>
              <Tooltip placement="top" title={value}>
                <div className={styles.date}>{value}</div>
              </Tooltip>
            </div>
          );
        },
        title: intl.formatMessage({
          id: "settings.formLayouts.grid.header.createdAt",
          defaultMessage: "Date Created",
          description: "Header of Date Created column",
        }),
        width: "15%",
      },
      {
        dataIndex: "user",
        key: "user",
        render: (user: UserRef) => (
          <UserPreviewCard userId={user.id}>
            <div>
              <AvatarWithName actionOnHover className={styles.avatar} user={user} />
            </div>
          </UserPreviewCard>
        ),
        title: intl.formatMessage({
          id: "settings.formLayouts.grid.header.user",
          defaultMessage: "Created By",
          description: "Header of Created By column",
        }),
        width: "20%",
      },
      {
        dataIndex: "id",
        key: "id",
        render: (id: number) => <AssociationsCell analyticsIssuer={analyticsIssuer} id={id} />,
        title: intl.formatMessage({
          id: "settings.formLayouts.grid.header.roles",
          defaultMessage: "Used By",
          description: "Header of Used By column",
        }),
        width: "12%",
      },
      {
        dataIndex: "id",
        key: "actions",
        render: (id: number, row: FormLayout) => (
          <Dropdown
            menu={{
              items: [
                {
                  disabled: row.default,
                  key: "setAsDefault",
                  label: intl.formatMessage({
                    id: "settings.formLayouts.grid.action.setAsDefault",
                    defaultMessage: "Set as Default",
                    description: "Set as Default action",
                  }),
                  onClick: () => onSetAsDefault(row),
                },
                {
                  key: "delete",
                  label: <DeleteLayoutAction disabled={row.default} id={id} showLabel />,
                },
              ],
            }}
            trigger={["click"]}
          >
            <MoreButton horizontal />
          </Dropdown>
        ),
        width: "5%",
      },
    ],
    [analyticsIssuer, intl, onSetAsDefault]
  );

  return (
    <>
      <Table<FormLayout>
        className={styles.table}
        columns={columns}
        dataSource={layouts}
        loading={loading}
        pagination={{ position: ["bottomCenter"] }}
        rowKey="id"
        scrollable
        showBorder
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  layouts: getLayouts(state),
  loading: isLoading(state),
});

const mapDispatchToProps = {
  onSetAsDefault: setAsDefault.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LayoutsTable);
